import posthog, { type CaptureResult, type PostHog } from "posthog-js";

// Public (client-side) PostHog credentials. The project API key is NOT a secret
// — it is designed to ship in the browser bundle — so it is read from the same
// VITE_* env pipeline as the Clerk/Convex public keys (see vite.config.ts,
// which also bridges these from the monorepo-root .env for prod builds).
const POSTHOG_KEY = import.meta.env.VITE_POSTHOG_KEY as string | undefined;
// Default to the managed reverse proxy (m.steerly.ai) rather than the direct
// us.i.posthog.com ingest host: routing through our own domain dodges
// ad/tracker blockers and lifts capture rates. Overridable via env.
const POSTHOG_HOST =
  (import.meta.env.VITE_POSTHOG_HOST as string | undefined) || "https://m.steerly.ai";

let initialized = false;

// The desktop sign-in handoff puts a bearer secret in the page URL
// (?desktopAuth=<requestId>): within its 5-minute TTL, whoever holds that id
// can fetch the org member's Clerk sign-in ticket through the unauthenticated
// awaitTicket query (convex/auth/desktopAuth.ts). PostHog copies the full
// location.href into event properties ($current_url, $referrer, the
// $initial_* person props, …), so every outgoing event is scrubbed via
// before_send below — the param stays in the address bar (the app's router
// reads it) but its value never reaches analytics.
const AUTH_HANDOFF_PARAM_RE = /([?&#]desktopAuth=)[^&#\s"']+/gi;

/**
 * Recursively redact desktopAuth param values from every string in a property
 * bag. Pure and export-tested; leaves non-string leaves (numbers, booleans,
 * Dates) untouched.
 */
export function scrubAuthHandoffParams<T>(value: T): T {
  if (typeof value === "string") {
    return value.replace(AUTH_HANDOFF_PARAM_RE, "$1redacted") as T;
  }
  if (Array.isArray(value)) {
    return value.map(scrubAuthHandoffParams) as T;
  }
  if (value && typeof value === "object" && !(value instanceof Date)) {
    const out: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(value)) out[k] = scrubAuthHandoffParams(v);
    return out as T;
  }
  return value;
}

/** `before_send` hook: scrub the bearer from every property surface. */
export function scrubEventBeforeSend(event: CaptureResult | null): CaptureResult | null {
  if (!event) return event;
  if (event.properties) event.properties = scrubAuthHandoffParams(event.properties);
  if (event.$set) event.$set = scrubAuthHandoffParams(event.$set);
  if (event.$set_once) event.$set_once = scrubAuthHandoffParams(event.$set_once);
  return event;
}

/**
 * Initialise the global PostHog client exactly once.
 *
 * Returns the configured client, or `null` when analytics is disabled — i.e.
 * no key is configured (local dev without `.env.local`, or the vitest jsdom
 * env) or there is no `window` (non-browser). Safe to call repeatedly; only the
 * first call with a key actually boots the SDK.
 *
 * Capture is enabled in BOTH the web funnel (steerly.ai / app.steerly.ai) and
 * the Tauri desktop shell — they share this bundle and there is no isTauri()
 * gate by design.
 */
export function initPostHog(): PostHog | null {
  if (initialized) return posthog;
  // Never boot the SDK under vitest (MODE === "test") — it would fire network +
  // storage side effects across the whole suite. The jsdom env has a `window`,
  // so the guard below is not enough on its own.
  if (import.meta.env.MODE === "test") return null;
  if (!POSTHOG_KEY) return null; // analytics disabled when no key is present
  if (typeof window === "undefined") return null; // SSR / non-browser guard
  initialized = true;
  // before_send only covers capture events. posthog-js ALSO persists the raw
  // location.href at init ($initial_person_info) and replays it as
  // $initial_current_url inside /flags request payloads, which never pass
  // through before_send. Scrub the bearer out of the address bar for the
  // synchronous duration of init so that persisted snapshot is clean, then
  // restore the URL — the browser-role tab needs ?desktopAuth=… to survive
  // the OAuth round-trip (SignInGate reads it after the provider redirect).
  withScrubbedAddressBar(bootPostHog);
  scrubPersistedInitialUrl();
  return posthog;
}

/**
 * Run `fn` (synchronous) with the desktopAuth bearer redacted from the
 * address bar, restoring the original URL afterwards — even if `fn` throws.
 * Exported for tests (initPostHog itself is inert under vitest).
 */
export function withScrubbedAddressBar<T>(fn: () => T): T {
  const rawUrl = window.location.href;
  const cleanUrl = scrubAuthHandoffParams(rawUrl);
  if (cleanUrl === rawUrl) return fn();
  window.history.replaceState(window.history.state, "", cleanUrl);
  try {
    return fn();
  } finally {
    window.history.replaceState(window.history.state, "", rawUrl);
  }
}

function bootPostHog(): void {
  posthog.init(POSTHOG_KEY as string, {
    api_host: POSTHOG_HOST,
    // With api_host pointed at our reverse proxy, ui_host tells the SDK where
    // the real PostHog app lives so generated links (session replay URLs, the
    // toolbar) resolve to us.posthog.com instead of the proxy domain.
    ui_host: "https://us.posthog.com",
    // Pin SDK default behaviours to a dated baseline (PostHog's recommended
    // setup) so future posthog-js upgrades don't silently change capture
    // defaults. Matches the snippet from the project's onboarding.
    defaults: "2026-05-30",
    // Only materialise person profiles for users we actually identify (signed
    // in via Clerk), not every anonymous visitor — cheaper and keeps anonymous
    // funnel traffic from inflating the person count.
    person_profiles: "identified_only",
    // Capture $pageview on SPA history changes so route transitions in the
    // pushState-based router (App.tsx) are tracked, not just the initial load.
    capture_pageview: "history_change",
    capture_pageleave: true,
    // Never let the desktop sign-in bearer (?desktopAuth=…) leave the SDK —
    // see scrubAuthHandoffParams above.
    before_send: scrubEventBeforeSend,
  });
}

/**
 * Repair a $initial_person_info persisted by an EARLIER visit (before the
 * init-time URL scrub existed): register_once means a raw ?desktopAuth=… URL
 * captured back then would otherwise keep feeding /flags payloads forever.
 * Reads/writes posthog-js internals, so it is defensive against shape drift.
 * The `client` param exists for tests; production callers pass nothing.
 */
export function scrubPersistedInitialUrl(client: unknown = posthog): void {
  try {
    const persistence = (
      client as {
        persistence?: {
          props?: Record<string, unknown>;
          register?: (props: Record<string, unknown>) => void;
        };
      }
    ).persistence;
    const info = persistence?.props?.["$initial_person_info"] as { u?: unknown } | undefined;
    if (!persistence?.register || typeof info?.u !== "string") return;
    const clean = scrubAuthHandoffParams(info.u);
    if (clean !== info.u) persistence.register({ $initial_person_info: { ...info, u: clean } });
  } catch {
    // Best-effort: a persistence shape change must never break app boot.
  }
}

/** The initialised PostHog client, or `null` when analytics is disabled. */
export function getPostHog(): PostHog | null {
  return initialized ? posthog : null;
}

/**
 * Minimal structural shape of the Clerk user we read for identify traits —
 * avoids coupling this module to Clerk's full `UserResource` type.
 */
export interface ClerkUserLike {
  primaryEmailAddress?: { emailAddress?: string | null } | null;
  fullName?: string | null;
}

/**
 * Build the PostHog identify trait bag from a Clerk user. Pure (no SDK side
 * effects) so it is unit-testable; only includes properties that are present.
 */
export function identifyTraitsFromClerkUser(user: ClerkUserLike): Record<string, unknown> {
  const traits: Record<string, unknown> = {};
  const email = user.primaryEmailAddress?.emailAddress;
  if (email) traits.email = email;
  if (user.fullName) traits.name = user.fullName;
  return traits;
}
